import { useState } from "react";
import { Plus, Pencil, Trash2, Landmark, Building2, Percent, CreditCard } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { computeMetrics, fmtMoney, fmtPct, uid } from "@/lib/finance";
import { PageHeader, SectionCard, Money, EmptyState, AddButton, KpiCard, ProgressBar } from "@/components/shared";
import { FormModal, TextField, NumberField, SelectField, TextArea } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const TYPES = [
  { value: "cash", label: "Cash" },
  { value: "investment", label: "Investment" },
  { value: "asset", label: "Asset" },
  { value: "debt", label: "Debt" },
];

const SUBTYPES = {
  cash: [
    { value: "checking", label: "Checking" },
    { value: "savings", label: "Savings" },
    { value: "high_yield_savings", label: "High-yield savings" },
    { value: "money_market", label: "Money market" },
    { value: "cd", label: "Certificate of deposit" },
    { value: "cash", label: "Physical cash" },
  ],
  investment: [
    { value: "brokerage", label: "Brokerage" },
    { value: "retirement", label: "Retirement (401k / IRA)" },
    { value: "roth", label: "Roth IRA" },
    { value: "hsa", label: "HSA" },
    { value: "crypto", label: "Crypto" },
    { value: "other", label: "Other" },
  ],
  asset: [
    { value: "real_estate", label: "Real estate" },
    { value: "vehicle", label: "Vehicle" },
    { value: "collectible", label: "Collectible" },
    { value: "business", label: "Business equity" },
    { value: "other", label: "Other" },
  ],
  debt: [
    { value: "credit_card", label: "Credit card" },
    { value: "mortgage", label: "Mortgage" },
    { value: "auto_loan", label: "Auto loan" },
    { value: "student_loan", label: "Student loan" },
    { value: "personal_loan", label: "Personal loan" },
    { value: "other", label: "Other" },
  ],
};

const GROUPS = [
  { type: "cash", label: "Cash" },
  { type: "investment", label: "Investments" },
  { type: "asset", label: "Assets" },
  { type: "debt", label: "Debt & liabilities" },
];

const rateLabel = (type) => (type === "debt" ? "Interest rate (APR %)" : type === "asset" ? "Growth rate (%)" : "Yield (APY %)");
const defaultSubtype = (type) => (SUBTYPES[type]?.[0]?.value || "");

export default function Accounts() {
  const { state, update, loading } = useFinancialState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const m = computeMetrics(state);
  const accounts = state.accounts || [];

  const blank = {
    id: uid(), name: "", type: "cash", subtype: defaultSubtype("cash"),
    institution: "", last4: "", balance: "", rate: "", creditLimit: "", notes: "",
  };
  const openNew = () => { setForm({ ...blank }); setOpen(true); };
  const openEdit = (a) => { setForm({ subtype: "", institution: "", last4: "", rate: "", creditLimit: "", notes: "", ...a }); setOpen(true); };

  const onTypeChange = (v) => setForm((f) => ({ ...f, type: v, subtype: defaultSubtype(v) }));

  const save = () => {
    if (!form.name) return toast.error("Account name is required");
    const acct = {
      ...form,
      // Debt/liability balances are stored as a positive "amount owed" (sign-agnostic entry).
      balance: form.type === "debt" ? Math.abs(Number(form.balance || 0)) : Number(form.balance || 0),
      rate: form.rate === "" ? "" : Number(form.rate),
      creditLimit: form.creditLimit === "" ? "" : Number(form.creditLimit),
      last4: String(form.last4 || "").replace(/\D/g, "").slice(-4),
    };
    update((prev) => {
      const exists = (prev.accounts || []).some((a) => a.id === acct.id);
      return { ...prev, accounts: exists ? prev.accounts.map((a) => (a.id === acct.id ? acct : a)) : [...(prev.accounts || []), acct] };
    }, `Saved account: ${acct.name}`);
    setOpen(false);
    toast.success("Account saved");
  };

  const remove = (id) => {
    update((prev) => ({ ...prev, accounts: prev.accounts.filter((a) => a.id !== id) }), "Deleted account");
    toast.success("Account removed");
  };

  const subtypeLabel = (a) => {
    const opt = (SUBTYPES[a.type] || []).find((s) => s.value === a.subtype);
    return opt ? opt.label : (a.subtype || "").replace(/_/g, " ");
  };

  return (
    <div>
      <PageHeader title="Accounts & debt" subtitle="All your balances, organized by type." testid="accounts-header"
        actions={<AddButton onClick={openNew} testid="add-account-button"><Plus className="h-4 w-4 mr-1.5" /> Add account</AddButton>} />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Cash" value={fmtMoney(m.cash)} testid="acct-kpi-cash" />
        <KpiCard label="Investments" value={fmtMoney(m.investments)} testid="acct-kpi-investments" />
        <KpiCard label="Assets" value={fmtMoney(m.assets)} testid="acct-kpi-assets" />
        <KpiCard label="Debt" value={fmtMoney(m.debt)} testid="acct-kpi-debt" />
      </div>

      {accounts.length === 0 ? (
        <SectionCard><EmptyState icon={Landmark} title="No accounts yet" message="Add your first account to start tracking your net worth." action={<AddButton onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add account</AddButton>} /></SectionCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {GROUPS.map((g) => {
            const items = accounts.filter((a) => a.type === g.type);
            if (!items.length) return null;
            const total = items.reduce((s, a) => s + Number(a.balance || 0), 0);
            return (
              <SectionCard key={g.type} title={g.label} right={<Money value={total} className="font-serif-display text-lg" />} testid={`account-group-${g.type}`}>
                <div className="space-y-2">
                  {items.map((a) => {
                    const util = a.type === "debt" && a.creditLimit > 0 ? Math.min(100, (Math.abs(Number(a.balance || 0)) / a.creditLimit) * 100) : null;
                    return (
                      <div key={a.id} className="rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`account-row-${a.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-[var(--text)] truncate">{a.name}</div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--text-faint)] mt-0.5">
                              {subtypeLabel(a) && <span className="capitalize">{subtypeLabel(a)}</span>}
                              {a.institution && <span className="inline-flex items-center gap-0.5"><Building2 className="h-3 w-3" />{a.institution}</span>}
                              {a.last4 && <span>••{a.last4}</span>}
                              {a.rate !== "" && a.rate != null && <span className="inline-flex items-center gap-0.5"><Percent className="h-3 w-3" />{fmtPct(a.rate, 2)} {a.type === "debt" ? "APR" : a.type === "asset" ? "growth" : "APY"}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Money value={a.balance} className="text-sm mr-1" />
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--danger)]" onClick={() => remove(a.id)} data-testid={`delete-account-${a.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                        {util != null && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-[10px] text-[var(--text-faint)] mb-1">
                              <span className="inline-flex items-center gap-0.5"><CreditCard className="h-3 w-3" />Utilization</span>
                              <span className="tabular-nums">{fmtMoney(Math.abs(a.balance))} / {fmtMoney(a.creditLimit)} · {fmtPct(util, 0)}</span>
                            </div>
                            <ProgressBar value={Math.abs(a.balance)} max={a.creditLimit} tone={util > 50 ? "danger" : util > 30 ? "gold" : "green"} />
                          </div>
                        )}
                        {a.notes && <p className="text-[11px] text-[var(--text-muted)] mt-1.5">{a.notes}</p>}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      {form && (
        <FormModal open={open} onOpenChange={setOpen} title={accounts.some((a) => a.id === form.id) ? "Edit account" : "Add account"}
          description="Add as much detail as you like — institution, rate, and credit limit power richer insights." onSubmit={save} testid="account-modal">
          <TextField label="Account name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} full testid="account-name-input" placeholder="e.g. Everyday Checking" />
          <SelectField label="Type" value={form.type} onChange={onTypeChange} options={TYPES} testid="account-type-select" />
          <SelectField label="Subtype" value={form.subtype} onChange={(v) => setForm({ ...form, subtype: v })} options={SUBTYPES[form.type] || []} testid="account-subtype-select" />
          <TextField label="Institution" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} placeholder="Chase, Fidelity…" testid="account-institution-input" />
          <TextField label="Last 4 digits" value={form.last4} onChange={(e) => setForm({ ...form, last4: e.target.value })} placeholder="1234" maxLength={4} testid="account-last4-input" />
          <NumberField label="Balance" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} testid="account-balance-input" hint={form.type === "debt" ? "Enter what you owe as a positive amount (e.g. 5000)" : "Current balance of this account"} />
          <NumberField label={rateLabel(form.type)} value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="0.00" testid="account-rate-input" />
          {form.type === "debt" && (
            <NumberField label="Credit limit" value={form.creditLimit} onChange={(e) => setForm({ ...form, creditLimit: e.target.value })} testid="account-limit-input" hint="For credit cards — enables utilization tracking" />
          )}
          <TextArea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} full rows={2} placeholder="Optional details…" testid="account-notes-input" />
        </FormModal>
      )}
    </div>
  );
}
